import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import moment from 'moment';
import { TranslateService } from '@ngx-translate/core';
import { StaffViewAttendanceService, StaffAttendanceRecord } from '../services/staff-view-attendance.service';
import { StaffSessionService } from '../services/staff-session.service';
import { STAFF_DEFAULT_SCREEN } from '../constant/staff-default-screen.config';

declare var customToastr: any;

const PAGE_LIMIT = 10;
const SEARCH_DEBOUNCE_MS = 500;
const DATE_FORMAT = 'YYYY-MM-DD';

@Component({
  selector: 'app-staff-view-attendance',
  templateUrl: './staff-view-attendance.component.html',
  styleUrls: ['../shared/staff-common.css', './staff-view-attendance.component.css']
})
export class StaffViewAttendanceComponent implements OnInit {
  staffId: number | string | null = null;

  selectedDate: string = moment().format(DATE_FORMAT);
  selectedDateObj: Date = moment().toDate();

  searchOpen = false;
  searchString = '';
  private searchDebounceTimer: any;
  private isFirstLoad = true;

  staffData: StaffAttendanceRecord[] = [];
  pageNumber = 1;
  maxPageNumber = 1;
  fetchingData = true;
  fetchingPaginationData = false;

  readonly skeletonRows = Array.from({ length: 10 }, () => ({}));

  constructor(
    private staffViewAttendanceService: StaffViewAttendanceService,
    private staffSession: StaffSessionService,
    private translate: TranslateService,
    private router: Router
  ) {}

  ngOnInit(): void {
    const staffDetails = this.staffSession.getStaffDetails();
    const info = this.staffSession.getSchoolAndStaffInfo();
    this.staffId = staffDetails?.staffId ?? info?.StaffId ?? null;
    this.loadStaffAttendance(1);
  }

  private get isLoading(): boolean {
    return this.fetchingData || this.fetchingPaginationData;
  }

  async loadStaffAttendance(page: number) {
    if (!this.staffId) { this.fetchingData = false; return; }
    try {
      const response = await firstValueFrom(this.staffViewAttendanceService.getStaffAttendanceInfo({
        staffId: this.staffId,
        attendanceDate: this.selectedDate,
        searchedStaffName: this.searchString,
        pageNumber: page,
        pageLimit: PAGE_LIMIT,
      }));
      if (response?.status && response.body) {
        const newRecords = response.body.staffAttendanceData || [];
        // Dedup by userId on append — guards against duplicate rows if a
        // page boundary shifts between requests (per spec).
        const existingIds = new Set(this.staffData.map(item => item.userId));
        const deduped = page === 1 ? newRecords : newRecords.filter(item => !existingIds.has(item.userId));
        this.staffData = page === 1 ? newRecords : [...this.staffData, ...deduped];
        this.maxPageNumber = response.body.maxPageNumber || 1;
        this.pageNumber = page;
      } else {
        this.staffData = [];
        const message = await firstValueFrom(this.translate.get('somethingWentWrong'));
        customToastr(response?.message || message, 'error');
      }
    } catch {
      this.staffData = [];
      const message = await firstValueFrom(this.translate.get('somethingWentWrong'));
      customToastr(message, 'error');
    } finally {
      this.fetchingData = false;
      this.fetchingPaginationData = false;
      this.isFirstLoad = false;
    }
  }

  loadMore() {
    if (this.pageNumber >= this.maxPageNumber) { return; }
    if (this.isLoading) { return; }
    this.fetchingPaginationData = true;
    this.loadStaffAttendance(this.pageNumber + 1);
  }

  refreshList() {
    this.staffData = [];
    this.pageNumber = 1;
    this.fetchingData = true;
    this.loadStaffAttendance(1);
  }

  // ---------- Search ----------

  toggleSearch() {
    this.searchOpen = !this.searchOpen;
  }

  // Scrolling the list closes the search box if it's open — matches the
  // mobile app's touch-based (not submit-based) dismiss.
  onListScroll() {
    if (this.searchOpen) { this.searchOpen = false; }
  }

  onSearchInput(value: string) {
    this.searchString = value;
    if (this.isFirstLoad) { return; }
    this.staffData = [];
    this.pageNumber = 1;
    this.fetchingData = true;
    clearTimeout(this.searchDebounceTimer);
    this.searchDebounceTimer = setTimeout(() => this.loadStaffAttendance(1), SEARCH_DEBOUNCE_MS);
  }

  // ---------- Date ----------

  onDateChange(date: Date) {
    if (this.isLoading) { return; }
    this.selectedDateObj = date;
    this.selectedDate = moment(date).format(DATE_FORMAT);
    this.staffData = [];
    this.pageNumber = 1;
    this.fetchingData = true;
    this.loadStaffAttendance(1);
  }

  // ---------- Display helpers ----------

  formatTime(value: string | null): string {
    return value ? moment(value).format('hh:mm A') : '--';
  }

  duration(record: StaffAttendanceRecord): string {
    if (!record.inTime || !record.outTime) { return '--'; }
    const start = moment(record.inTime);
    const end = moment(record.outTime);
    const totalMinutes = Math.max(0, end.diff(start, 'minutes'));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}h ${minutes}m`;
  }

  goBack() {
    this.router.navigate(['/staff', STAFF_DEFAULT_SCREEN]);
  }
}
